import React, { useEffect, useRef, useState } from 'react'
import { portal } from '../portalApi.js'
import { currentCompany, companyPath, HOME_COMPANY } from '../company.js'
import { fmt } from '../api.js'
import NoteDrawer from '../components/NoteDrawer.jsx'

// 손익계산서 — 위하고에서 받은 파일을 그대로 담아 보여준다. 우리가 계산하지 않는다.
// 시트 이름이 곧 사업부다(전체 · 에르모어 · 링크맘). 「전체」는 위하고가 준 회사 전체이고
// 우리가 더한 것이 아니다 - 두 사업부 합과 꼭 같지도 않다(어느 쪽에도 안 달린 공통비).
//
// 대시보드는 전체·에르모어·링크맘을 **한 화면에 나란히** 세운다. 탭으로만 보면 임원이
// 셋을 번갈아 눌러 머릿속에서 견줘야 한다. 그 아래가 고른 탭의 상세다.

const MONTHS = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12]
const 백만 = (v) => (v ? Math.round(v / 1e6).toLocaleString() : (v === 0 ? '' : ''))

export default function Pnl({ year, currentUserId, autoOpenNoteId, onAutoOpened }) {
  const [y, setY] = useState(year || new Date().getFullYear())
  const [보고서, set보고서] = useState(null)   // {만드는중} | {id, 제목} | {error}
  const [data, setData] = useState(null)
  const [div, setDiv] = useState(null)
  const [unit, setUnit] = useState('백만')
  const [view, setView] = useState('board')   // board=대시보드 · table=계정과목 표
  const [err, setErr] = useState('')
  const [busy, setBusy] = useState(false)
  const [toast, setToast] = useState('')
  const fileRef = useRef(null)

  const notify = (m) => { setToast(m); setTimeout(() => setToast(''), 2500) }

  // 브라우저는 문서 제목을 PDF 기본 파일 이름으로 쓴다. 이름 없이 뽑으면 「ERP」 로 저장돼
  // 올해 것인지 어느 사업부 것인지 파일명으로는 알 수 없다. 인쇄가 끝나면 제목을 되돌린다.
  const savePdf = () => {
    const 이전 = document.title
    document.title = `손익계산서_${y}_${div || ''}${view === 'table' ? '_표' : ''}`
    const 되돌림 = () => { document.title = 이전; window.removeEventListener('afterprint', 되돌림) }
    window.addEventListener('afterprint', 되돌림)
    window.print()
  }

  // 지금 보고 있는 회사. 자료가 없을 때 어느 회사 이야기인지 적어 주려는 것뿐이다.
  const 회사코드 = currentCompany()
  const [회사이름, set회사이름] = useState(회사코드 === HOME_COMPANY ? '주식회사 에르모어' : 회사코드)
  useEffect(() => {
    portal.get('/api/meta/companies')
      .then((r) => {
        const c = (r.companies || r || []).find((x) => x.code === 회사코드)
        if (c && c.name) set회사이름(c.name)
      })
      .catch(() => {})
  }, [])

  const load = () => {
    setErr(''); setData(null)
    portal.get(`/api/pnl?year=${y}`)
      .then((r) => { setData(r); setDiv((d) => (r['사업부'].includes(d) ? d : r['사업부'][0] || null)) })
      .catch((e) => setErr(e.status === 403
        ? '손익계산서 화면 권한이 없습니다. 관리자에게 권한을 요청하세요.' : e.message))
  }
  useEffect(() => { load() }, [y])

  const upload = async (f) => {
    if (!f) return
    setBusy(true); setErr('')
    try {
      const fd = new FormData()
      fd.append('file', f)
      fd.append('year', String(y))
      const res = await fetch('/api/pnl/upload', { method: 'POST', body: fd, credentials: 'same-origin' })
      const body = await res.json().catch(() => ({}))
      if (!res.ok) throw new Error(body.error || res.statusText)
      // 시트 이름이 법인이면 그 법인 장부로 들어간다. 어디로 갔는지 말해 주지 않으면
      // 「올렸는데 화면이 그대로」로 보인다 - 지금 보는 회사에는 안 들어갔을 수 있다.
      const 간곳 = Object.values(body['회사별'] || {}).map(
        (c) => `${c['회사']} ${Object.entries(c['줄수'] || {}).map(([k, v]) => `${k} ${v}줄`).join('·')}`)
      notify(간곳.join(' / ') || '올렸습니다')
      if ((body['막힌것'] || []).length) {
        setErr('볼 권한이 없어 넣지 않은 시트: '
          + body['막힌것'].map((x) => `${x['시트']}(${x['회사']})`).join(', '))
      }
      load()
    } catch (e) { setErr(e.message) } finally {
      setBusy(false)
      if (fileRef.current) fileRef.current.value = ''
    }
  }

  const rows = (data && div && data['데이터'][div]) || []
  // 지표는 이미 받아 온 줄에서 뽑는다 - 서버를 한 번 더 부를 이유가 없다
  const 찾기 = (list, ...조각) => {
    const r = (list || []).find((x) => x.level === 0 && 조각.every((c) => x['계정과목'].includes(c)))
    return r || null
  }
  const 값 = (list, ...조각) => { const r = 찾기(list, ...조각); return r ? r['합계'] : null }
  const 영업줄 = (list) => 찾기(list, '영업이익') || 찾기(list, '영업손실')
  // 위하고는 흑자·적자에 따라 줄 이름을 바꿔 보낸다(영업이익 ↔ 영업손실). 손실 줄은 양수로
  // 오므로 부호를 우리가 돌려야 한다 - 안 돌리면 적자가 흑자로 보인다.
  const 부호 = (r) => (r && r['계정과목'].includes('손실') ? -1 : 1)
  const 영업값 = (list) => {
    const r = 영업줄(list)
    return r ? 부호(r) * r['합계'] : null
  }
  // 당기순손익은 이름이 셋이다 — 전체 시트는 「당기순이익/당기순손실」, 사업부 시트는
  // 위하고가 Ⅹ 자리에 「프로젝트별손익현황」을 넣어 보낸다(값은 당기순손익이 맞다).
  const 순익줄 = (list) => (찾기(list, '당기순이익') || 찾기(list, '당기순손실')
    || 찾기(list, '프로젝트별손익'))
  const 순익값 = (list) => {
    const r = 순익줄(list)
    return r ? 부호(r) * r['합계'] : null
  }
  const 지표 = [
    ['매출액', 값(rows, '매출액')],
    ['매출총이익', 값(rows, '매출총이익')],
    ['판매비와 관리비', 값(rows, '판매비')],
    ['영업손익', 영업값(rows)],
    ['당기순손익', 순익값(rows)],
  ]
  // 값이 있는 달만 보여준다 - 상반기만 받은 파일에 7~12월 빈 칸이 늘어서면 읽기 어렵다
  const 쓰는달 = MONTHS.filter((m) => rows.some((r) => r[`m${m}`]))
  const show = (v) => {
    if (!v) return v === 0 ? '' : ''
    return unit === '백만' ? 백만(v) : fmt(Math.round(v))
  }

  // ── 대시보드용 파생값 ─────────────────────────────
  const 매출줄 = 찾기(rows, '매출액')
  const 총이익줄 = 찾기(rows, '매출총이익')
  const 판관줄 = 찾기(rows, '판매비')
  const 영업 = 영업줄(rows)
  const 영업부호 = 영업 && 영업['계정과목'].includes('손실') ? -1 : 1
  const 월값 = (r, m, sign = 1) => (r ? sign * (r[`m${m}`] || 0) : 0)
  const 최대 = Math.max(1, ...쓰는달.map((m) => Math.abs(월값(매출줄, m))))

  // 매출과 원가가 짝을 이루는 구조. 재무팀이 알려 준 규칙이다 —
  //   상품매출 ↔ 상품매출원가 · 제품매출 ↔ 제품매출원가 · 특정매입 매출 ↔ 특정매입원가
  //   그 밖의 매출(임대료·수수료·플랫폼·가맹비·기타·해외구매대행)은 **원가가 없는 수수료**다.
  // 특정매입은 총매출에서 원가를 뺀 **순액만** 매출액에 들어간다 - 그래서 매출 아래 줄을
  // 그냥 더하면 매출액의 두 배가 된다(에르모어 46,127 ↔ 22,954백만). 이름이 시트마다
  // 다르다: 사업부는 「수수료매출(특정매입)·특정매입원가」, 전체는 「내부매출액(특수관계)·
  // 특수관계매입액」. 그래서 이름이 아니라 생김새로 찾는다.
  const 줄 = (list, 이름) => (list || []).find((r) => r['계정과목'] === 이름) || null
  const 금액 = (list, 이름) => { const r = 줄(list, 이름); return r ? r['합계'] : 0 }
  const 매출구조 = (list) => {
    const 매출액 = 값(list, '매출액') || 0
    const 특매 = (list || []).find((r) => /특정매입|특수관계/.test(r['계정과목'])
      && /매출/.test(r['계정과목']))
    const 특원 = (list || []).find((r) => /특정매입원가|특수관계매입액/.test(r['계정과목']))
    const 특순 = (특매 ? 특매['합계'] : 0) - (특원 ? 특원['합계'] : 0)
    const 상품 = { 이름: '상품', 매출: 금액(list, '상품매출'), 원가: 금액(list, '상품매출원가') }
    const 제품 = { 이름: '제품', 매출: 금액(list, '제품매출'), 원가: 금액(list, '제품매출원가') }
    const 수수료 = { 이름: '특정매입 수수료', 매출: 특순, 원가: 0 }
    const 기타 = { 이름: '그 밖의 수수료', 매출: 매출액 - 상품.매출 - 제품.매출 - 특순, 원가: 0 }
    return [상품, 제품, 수수료, 기타]
      .map((x) => ({ ...x, 이익: x.매출 - x.원가 }))
      .filter((x) => Math.abs(x.매출) >= 1e6 || Math.abs(x.이익) >= 1e6)
      .sort((a, b) => b.이익 - a.이익)
  }

  // 재고는 손익계산서 안에 있다 - 매출원가 밑의 「기말 상품/제품 재고액」이 그 달 말 잔액이다.
  const 재고줄들 = (list) => ({
    상품: 줄(list, '기말 상품 재고액'),
    제품: 줄(list, '기말 제품 재고액'),
  })

  // 판관비 상위 - 소계(level 0) 다음에 오는 세부 항목들만 본다
  const 판관상위뽑기 = (list, 몇개 = 8) => {
    const 소계 = 찾기(list, '판매비')
    const i = (list || []).indexOf(소계)
    if (i < 0) return []
    const out = []
    for (let k = i + 1; k < list.length && list[k].level !== 0; k++) {
      if (list[k]['합계']) out.push(list[k])
    }
    return out.sort((a, b) => b['합계'] - a['합계']).slice(0, 몇개)
  }
  const 판관상위 = 판관상위뽑기(rows)
  const 판관합 = 판관줄 ? 판관줄['합계'] : 0

  const 사업부표 = (data ? data['사업부'] : []).map((d) => {
    const rs = data['데이터'][d]
    return { 이름: d, 매출: 값(rs, '매출액') || 0, 총이익: 값(rs, '매출총이익') || 0, 영업: 영업값(rs) || 0 }
  })
  const 사업부최대 = Math.max(1, ...사업부표.map((x) => Math.abs(x.매출)))
  const 률 = (a, b) => (b ? (a / b * 100) : null)

  // ── 전체·사업부를 한 화면에 나란히 (요청: 전체도, 에르모어·링크맘도 각각)
  const 달전체 = MONTHS.filter((m) => (data ? data['사업부'] : [])
    .some((d) => (data['데이터'][d] || []).some((r) => r[`m${m}`])))
  const 기간글 = 달전체.length
    ? `${달전체[0]}~${달전체[달전체.length - 1]}월 누계` : ''
  const 카드들 = (data ? data['사업부'] : []).map((d) => {
    const rs = data['데이터'][d] || []
    const 매출 = 찾기(rs, '매출액')
    const 영업 = 영업줄(rs)
    const 순익 = 순익줄(rs)
    return {
      이름: d,
      전체인가: d === '전체',
      매출: 값(rs, '매출액') || 0,
      총이익: 값(rs, '매출총이익') || 0,
      판관: 값(rs, '판매비') || 0,
      영업: 영업값(rs) || 0,
      순익: 순익값(rs) || 0,
      월매출: 달전체.map((m) => (매출 ? 매출[`m${m}`] || 0 : 0)),
      월영업: 달전체.map((m) => (영업 ? 부호(영업) * (영업[`m${m}`] || 0) : 0)),
    }
  })
  // 분석 문장은 **숫자에서 만든다**. 손으로 적어 두면 다음 달 파일을 올렸을 때 글만
  // 옛말로 남고, 읽는 사람은 그 글을 믿는다. 조건도 숫자로 가린다(흑자/적자, 나아짐/나빠짐).
  //
  // 말은 **사업부를 겨누지 않는다.** 이 글은 사업부장들이 함께 보는 자리에 걸린다 -
  // 「A 의 흑자를 B 가 지운다」처럼 적으면 숫자가 맞아도 읽는 자리에서 싸움이 된다.
  // 사업 성격과 성장 단계가 다르면 원가율도 판관비율도 다르다. 사실만 나란히 놓고,
  // 판단은 보는 사람이 하게 둔다.
  // 사업부 한 곳을 같은 잣대로 읽는다. 전사와 같은 함수를 쓰므로 기준이 갈리지 않는다.
  // 매출 구성은 넣지 않는다 - 사업부 시트의 매출 아래에는 「수수료매출(특정매입)」·
  // 「특정매입원가」 같은 참고 줄이 섞여 있어 그대로 더하면 매출액과 맞지 않는다
  // (에르모어 자식 합 46,127백만 ↔ 매출액 22,954백만). 잘못 읽히느니 안 적는다.
  const 사업부분석 = (c) => {
    const M = (v) => Math.round(v / 1e6).toLocaleString()
    const P = (a, b) => (b ? (a / b * 100).toFixed(1) : '—')
    const out = [[
      `매출 ${M(c.매출)}백만 · 영업손익 ${M(c.영업)}백만 (${P(c.영업, c.매출)}%)`,
      `${기간글} 기준입니다. 당기순손익은 ${M(c.순익)}백만입니다.`,
    ], [
      `매출총이익률 ${P(c.총이익, c.매출)}% · 판관비율 ${P(c.판관, c.매출)}%`,
      `둘의 차이 ${P(c.총이익 - c.판관, c.매출)}%p 가 그대로 영업손익률입니다.`,
    ]]
    const 월 = c.월영업 || []
    if (월.length) {
      const 최고 = 월.indexOf(Math.max(...월))
      const 최저 = 월.indexOf(Math.min(...월))
      const 평 = (a) => (a.length ? a.reduce((x, y) => x + y, 0) / a.length : 0)
      let 뒤말 = ''
      if (월.length >= 6) {
        const 뒤 = 월.slice(-3)
        const 앞 = 월.slice(0, -3)
        뒤말 = ` 최근 3개월 평균은 ${M(평(뒤))}백만으로 그 앞 ${M(평(앞))}백만보다 `
          + `${평(뒤) < 평(앞) ? '낮습니다' : '높습니다'}.`
      }
      out.push([
        `월별 영업손익은 ${달전체[최고]}월 ${M(월[최고])}백만이 가장 높고 `
        + `${달전체[최저]}월 ${M(월[최저])}백만이 가장 낮습니다`,
        `${월.length}개 달 가운데 ${월.filter((v) => v < 0).length}개가 마이너스입니다.${뒤말}`,
      ])
    }
    const 몫 = 매출구조((data && data['데이터'][c.이름]) || [])
    if (몫.length && c.총이익) {
      out.push([
        `매출총이익 ${M(c.총이익)}백만은 `
        + `${몫.map((x) => `${x.이름} ${M(x.이익)}(${P(x.이익, c.총이익)}%)`).join(' · ')}`,
        몫.filter((x) => x.원가).map((x) => `${x.이름} 이익률 ${P(x.이익, x.매출)}%`).join(' · ')
        + '. 나머지는 원가 없는 수수료성 매출입니다.',
      ])
    }
    const 상위 = 판관상위뽑기((data && data['데이터'][c.이름]) || [], 3)
    if (상위.length && c.판관) {
      out.push([
        `판관비는 ${상위.slice(0, 2).map((r) => `${r['계정과목']} ${M(r['합계'])}백만`).join(', ')}이 `
        + `${P(상위.slice(0, 2).reduce((a, r) => a + r['합계'], 0), c.판관)}%를 차지합니다`,
        `상위 ${상위.length}개 합이 판관비의 `
        + `${P(상위.reduce((a, r) => a + r['합계'], 0), c.판관)}%입니다.`,
      ])
    }
    return out
  }

  const 분석줄들 = (() => {
    if (!카드들.length) return []
    const 전 = 카드들.find((c) => c.전체인가) || 카드들[0]
    const 사 = 카드들.filter((c) => !c.전체인가)
    const M = (v) => Math.round(v / 1e6).toLocaleString()
    const P = (a, b) => (b ? (a / b * 100).toFixed(1) : '—')
    const out = []

    out.push([
      `${기간글} 매출 ${M(전.매출)}백만 · 영업손익 ${M(전.영업)}백만 (${P(전.영업, 전.매출)}%)`,
      `전사 기준 ${전.영업 < 0 ? '영업적자' : '영업흑자'}입니다. 영업외수지와 법인세까지 `
      + `반영한 당기순손익은 ${M(전.순익)}백만입니다.`,
    ])

    if (사.length) {
      out.push([
        `사업부별 영업손익 — ${사.map((c) => `${c.이름} ${M(c.영업)}`).join(' · ')}백만`,
        `전사 손익은 두 사업부를 합한 결과입니다. 사업 성격과 성장 단계가 달라 `
        + `손익 구조도 다릅니다.`,
      ])
    }

    if (사.length >= 2) {
      out.push([
        `매출총이익률 — 전사 ${P(전.총이익, 전.매출)}% `
        + `(${사.map((c) => `${c.이름} ${P(c.총이익, c.매출)}%`).join(' · ')})`,
        `취급 상품과 사업 모델이 다르면 원가율도 다릅니다. 우열이 아니라 구조 차이로 봅니다.`,
      ])
    }

    // 매출이 없는 회사(설립 첫해 같은)는 비율이 뜻이 없다 - 「—%」만 늘어놓지 않는다.
    out.push(전.매출 ? [
      `판관비율 ${P(전.판관, 전.매출)}% · 매출총이익률 ${P(전.총이익, 전.매출)}% — 그 차이가 영업손익률`,
      사.length
        ? 사.map((c) => `${c.이름} 판관비율 ${P(c.판관, c.매출)}% / 매출총이익률 ${P(c.총이익, c.매출)}%`)
          .join(' · ') + '.'
        : `판관비는 ${M(전.판관)}백만입니다.`,
    ] : [
      `판관비 ${M(전.판관)}백만 · 영업손익 ${M(전.영업)}백만`,
      '아직 매출이 없어 비율은 내지 않습니다.',
    ])

    const 월 = 전.월영업 || []
    if (월.length) {
      const 최고 = 월.indexOf(Math.max(...월))
      const 최저 = 월.indexOf(Math.min(...월))
      const 평 = (a) => (a.length ? a.reduce((x, y) => x + y, 0) / a.length : 0)
      let 뒤말 = ''
      if (월.length >= 6) {
        const 뒤 = 월.slice(-3)
        const 앞 = 월.slice(0, -3)
        뒤말 = ` 최근 3개월 평균은 ${M(평(뒤))}백만으로 그 앞 ${M(평(앞))}백만보다 `
          + `${평(뒤) < 평(앞) ? '낮습니다' : '높습니다'}.`
      }
      out.push([
        `월별 영업손익(전사)은 ${달전체[최고]}월 ${M(월[최고])}백만이 가장 높고 `
        + `${달전체[최저]}월 ${M(월[최저])}백만이 가장 낮습니다`,
        `${월.length}개 달 가운데 ${월.filter((v) => v < 0).length}개가 마이너스입니다.${뒤말}`,
      ])
    }

    const 몫 = 매출구조((data && data['데이터'][전.이름]) || [])
    if (몫.length && 전.총이익) {
      out.push([
        `매출총이익 ${M(전.총이익)}백만의 출처 — `
        + `${몫.map((x) => `${x.이름} ${M(x.이익)}(${P(x.이익, 전.총이익)}%)`).join(' · ')}`,
        `상품·제품은 각각 매출원가와 짝을 이루고, 특정매입은 총매출에서 원가를 뺀 순액이 `
        + `매출로 잡힙니다. 그 밖의 매출은 원가가 없는 수수료성입니다.`,
      ])
    }
    const 상위 = 판관상위뽑기((data && data['데이터'][전.이름]) || [], 3)
    if (상위.length && 전.판관) {
      const 둘 = 상위.slice(0, 2)
      out.push([
        `전사 판관비는 ${둘.map((r) => `${r['계정과목']} ${M(r['합계'])}백만`).join(', ')}이 `
        + `${P(둘.reduce((a, r) => a + r['합계'], 0), 전.판관)}%를 차지합니다`,
        `상위 ${상위.length}개 합이 판관비의 `
        + `${P(상위.reduce((a, r) => a + r['합계'], 0), 전.판관)}%입니다.`,
      ])
    }
    return out
  })()

  // 위하고의 「전체」는 두 사업부 합과 꼭 같지 않다. 다르면 얼마나 다른지 적어 둔다 -
  // 보는 사람이 더해 보고 「안 맞는다」고 할 자리다.
  const 합차이 = (() => {
    const 전 = 카드들.find((c) => c.전체인가)
    const 나머지 = 카드들.filter((c) => !c.전체인가)
    if (!전 || 나머지.length < 2) return null
    const 합 = 나머지.reduce((a, c) => a + c.영업, 0)
    return Math.abs(전.영업 - 합) >= 1 ? 전.영업 - 합 : null
  })()


  return (
    <>
      <div style={{ position: 'relative' }} className="no-print">
        <h2>손익계산서</h2>
        <NoteDrawer target="pnl" screenKey="closing_pnl" context={{ year: y }} contextLabel={String(y)}
          autoOpenNoteId={autoOpenNoteId} onAutoOpened={onAutoOpened} currentUserId={currentUserId} />
      </div>

      {보고서 && !보고서.만드는중 && (
        <div className={'hint no-print' + (보고서.error ? ' error-box' : '')}
          style={{ marginBottom: 10 }}>
          {보고서.error
            ? `보고서를 만들지 못했습니다 — ${보고서.error}`
            : <>보고서 <b>{보고서.제목}</b> 를 만들었습니다. 「보고서」 메뉴에서 속성을 채운 뒤 넘기세요.</>}
        </div>
      )}

      <div className="row no-print" style={{ marginBottom: 10, flexWrap: 'wrap' }}>
        <span className="muted">연도</span>
        <select value={y} onChange={(e) => setY(Number(e.target.value))}>
          {[y + 1, y, y - 1, y - 2].filter((v, i, a) => a.indexOf(v) === i)
            .sort((a, b) => b - a).map((v) => <option key={v} value={v}>{v}년</option>)}
        </select>
        <span className="seg">
          {[['board', '대시보드'], ['table', '계정과목 표']].map(([k, l]) => (
            <label key={k} className={view === k ? 'on' : ''}>
              <input type="radio" name="pnlview" checked={view === k}
                onChange={() => setView(k)} />{l}
            </label>
          ))}
        </span>
        <select value={unit} onChange={(e) => setUnit(e.target.value)} disabled={view === 'board'}>
          <option value="백만">백만원</option>
          <option value="원">원</option>
        </select>
        <input ref={fileRef} type="file" accept=".xlsx,.xls" disabled={busy}
          onChange={(e) => upload(e.target.files?.[0])} />
        <span className="muted" style={{ fontSize: 12 }}>
          {busy ? '읽는 중…'
            : data?.['최근업로드']
              ? `${data['최근업로드']['파일명'] || ''} · ${String(data['최근업로드']['uploaded_at']).slice(0, 16).replace('T', ' ')}`
              : '위하고 손익계산서 파일을 올려주세요'}
        </span>
      </div>

      {/* 아직 결산이 안 끝난 숫자다. 임원 보고로 나가는 화면이라 숫자보다 먼저 보여야 한다 -
          확정본으로 읽고 결정하면 되돌릴 수 없다. */}
      {/* 종이에서는 화면 머리줄(연도·탭·파일 선택)이 모두 사라지므로, 무엇을 뽑은 것인지가
          여기에만 남는다. 어느 회사·연도·사업부이고 어느 파일 기준인지 적는다. */}
      {data && data['사업부'].length > 0 && (
        <div className="print-only print-head">
          <div className="t">손익계산서 · {회사이름} · {y}년 {div}</div>
          <div className="s">
            {기간글 ? `${기간글} · ` : ''}{view === 'table' && unit === '원' ? '단위 원' : '단위 백만원'}
            {data['최근업로드']
              ? ` · 위하고 ${data['최근업로드']['파일명'] || ''} (${String(data['최근업로드']['uploaded_at']).slice(0, 10)} 업로드) 기준`
              : ''}
          </div>
          <div className="line" />
        </div>
      )}

      <div className="pnl-draft">
        <b>가결산입니다 — 확정된 숫자가 아닙니다.</b>
        <span>
          결산이 끝나면 바뀔 수 있습니다{기간글 ? ` (${y}년 ${기간글})` : ''}.
          대외 제출이나 확정 보고에는 결산 완료 뒤의 숫자를 쓰세요.
        </span>
      </div>

      <div className="hint no-print" style={{ marginBottom: 12 }}>
        위하고에서 받은 손익계산서를 그대로 보여줍니다 — 이 화면은 계산하지 않습니다.
        시트 이름이 곧 사업부이고, <b>「전체」는 위하고가 준 회사 전체</b>입니다(우리가 더한 것이 아닙니다).
        시트 이름이 <b>다른 법인</b>이면(동백·베이비버스·베이비플러스) 그 법인 장부로 들어갑니다 —
        올린 뒤 왼쪽 위에서 회사를 바꿔 보세요.
      </div>

      {err && <div className="error-box">{err}</div>}
      {!err && !data && <div className="muted">불러오는 중…</div>}
      {data && !data['사업부'].length && (
        <div className="panel" style={{ padding: 20 }}>
          <b>{회사이름}의 {y}년 자료가 없습니다.</b>
          <div className="muted" style={{ marginTop: 6, fontSize: 13 }}>
            {/* 법인마다 장부가 따로다. 회사를 바꿔 둔 줄 모르고 「대시보드가 안 뜬다」로
                읽히는 자리라, 지금 보고 있는 회사를 이름으로 적고 길을 같이 둔다. */}
            손익계산서는 <b>법인마다 따로</b>입니다. 지금 보고 있는 회사는{' '}
            <b>{회사이름}</b>이고, 이 법인으로 올린 파일이 아직 없습니다.
            {회사코드 !== HOME_COMPANY && (
              <> 에르모어 손익을 보시려면{' '}
                <a href={companyPath(HOME_COMPANY)}>주식회사 에르모어로 바꾸세요</a>
                {' '}(왼쪽 위 회사 선택).
              </>
            )}
            <div style={{ marginTop: 6 }}>
              이 법인 자료가 있으면 위의 파일 선택으로 올려주세요 —
              <b> 시트 이름이 그대로 탭이 됩니다</b>(「전체」 시트가 있으면 회사 전체로 씁니다).
            </div>
          </div>
        </div>
      )}

      {data && data['사업부'].length > 0 && (
        <>
          <div className="data-tabs no-print">
            {data['사업부'].map((d) => (
              <button key={d} type="button"
                className={'data-tab' + (div === d ? ' active' : '')}
                onClick={() => setDiv(d)}>
                {d}{d === '전체' ? ' (회사)' : ''}
              </button>
            ))}
            <button type="button" className="btn secondary" style={{ marginLeft: 'auto' }}
              title="브라우저 인쇄 창에서 대상을 'PDF로 저장'으로 고르세요. 지금 보고 있는 보기(대시보드/표)와 사업부가 그대로 나옵니다."
              onClick={savePdf}>PDF</button>
            {/* 지금 보고 있는 연도·사업부 그대로 보고서가 된다. 고르고 나서 누르면 된다. */}
            <button type="button" className="btn"
              disabled={보고서?.만드는중}
              title={`${y}년 ${div || ''} 손익계산서를 보고서로 만듭니다`}
              onClick={async () => {
                set보고서({ 만드는중: true })
                try {
                  set보고서(await portal.post('/api/reports/build',
                    { kind: 'pnl', year: y, division: div }))
                } catch (e) { set보고서({ error: e.message }) }
              }}>
              {보고서?.만드는중 ? '만드는 중…' : '보고서 만들기'}
            </button>
          </div>
          {view === 'board' && (
            <div className="pnl-board">
              <div className="pb-cards">
                {카드들.map((c) => {
                  const 최대매출 = Math.max(1, ...c.월매출.map(Math.abs))
                  const 최대영업 = Math.max(1, ...c.월영업.map(Math.abs))
                  return (
                    <div key={c.이름} className={'panel pb-card' + (c.전체인가 ? ' lead' : '')}>
                      <div className="pb-top">
                        <b>{c.이름}</b>
                        <span className="muted">{c.전체인가 ? '회사 전체' : '사업부'}</span>
                      </div>
                      <div className="pb-sales">
                        {Math.round(c.매출 / 1e6).toLocaleString()}<small>백만</small>
                      </div>
                      <div className="pb-sub">매출액 · {기간글}</div>
                      <div className="pb-lines">
                        {[['매출총이익', c.총이익, true], ['판매비와 관리비', c.판관, true],
                          ['영업손익', c.영업, false], ['당기순손익', c.순익, false]].map(
                          ([k, v, 비율]) => (
                            <div key={k} className="pb-line">
                              <span className="k">{k}</span>
                              <span className={'v' + (v < 0 ? ' neg' : '')}>
                                {Math.round(v / 1e6).toLocaleString()}
                              </span>
                              <span className={'r' + (v < 0 ? ' neg' : '')}>
                                {c.매출 ? `${(v / c.매출 * 100).toFixed(1)}%` : '—'}
                              </span>
                            </div>
                          ))}
                      </div>
                      {/* 달마다 어떻게 움직였는지 - 누계만 보면 7월에 꺾인 것이 안 보인다 */}
                      <div className="pb-spark">
                        <div className="pb-sk">
                          <span className="t">월별 매출</span>
                          <div className="bars">
                            {c.월매출.map((v, i) => (
                              <i key={i} style={{ height: `${Math.abs(v) / 최대매출 * 100}%` }}
                                title={`${달전체[i]}월 ${Math.round(v / 1e6).toLocaleString()}백만`} />
                            ))}
                          </div>
                        </div>
                        <div className="pb-sk">
                          <span className="t">월별 영업손익</span>
                          <div className="bars mid">
                            {c.월영업.map((v, i) => (
                              <i key={i} className={v < 0 ? 'neg' : ''}
                                style={{ height: `${Math.abs(v) / 최대영업 * 50}%` }}
                                title={`${달전체[i]}월 ${Math.round(v / 1e6).toLocaleString()}백만`} />
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  )
                })}
              </div>
              {합차이 != null && (
                <div className="hint" style={{ marginTop: -4, marginBottom: 12 }}>
                  「전체」의 영업손익은 사업부 둘을 더한 것과 <b>{fmt(Math.round(합차이))}원</b> 다릅니다 —
                  어느 사업부에도 달리지 않은 공통비입니다. 위하고가 준 전체를 그대로 씁니다.
                </div>
              )}

              <div className="pb-sel no-print">아래는 <b>{div}</b> 상세입니다 — 위 탭으로 바꿉니다.</div>
              <div className="ov-row" style={{ gridTemplateColumns: 'repeat(5, 1fr)' }}>
                {지표.map(([k, v]) => (
                  <div key={k} className={'ov' + (k === '매출액' ? ' ov-lead' : '')}>
                    <div className="ov-k">{k}</div>
                    <div className={'ov-v sm' + (v != null && v < 0 ? ' bad' : '')}>
                      {v == null ? '—' : Math.round(v / 1e6).toLocaleString()}<small>백만</small>
                    </div>
                    <div className="ov-d">
                      {k === '매출총이익' && 률(v, 값(rows, '매출액')) != null
                        ? `매출총이익률 ${률(v, 값(rows, '매출액')).toFixed(1)}%` : ''}
                      {k === '영업손익' && 률(v, 값(rows, '매출액')) != null
                        ? `매출 대비 ${률(v, 값(rows, '매출액')).toFixed(1)}%` : ''}
                      {k === '판매비와 관리비' && 률(v, 값(rows, '매출액')) != null
                        ? `매출 대비 ${률(v, 값(rows, '매출액')).toFixed(1)}%` : ''}
                    </div>
                  </div>
                ))}
              </div>

              <div className="panel" style={{ padding: 16, marginBottom: 12 }}>
                <b>월별 추이</b>
                <span className="muted" style={{ fontSize: 12, marginLeft: 8 }}>
                  막대는 매출액, 아래 두 줄은 매출총이익과 영업이익 (백만원)
                </span>
                <div className="mchart">
                  {!!쓰는달.length && (
                    <div className="mcol mkeys">
                      <div className="mbars" />
                      <div className="mlab">&nbsp;</div>
                      <div className="mval">매출액</div>
                      <div className="mval sub">매출총이익</div>
                      <div className="mval sub">영업이익</div>
                    </div>
                  )}
                  {쓰는달.map((m) => {
                    const s = 월값(매출줄, m)
                    const g = 월값(총이익줄, m)
                    const o = 월값(영업, m, 영업부호)
                    return (
                      <div key={m} className="mcol">
                        <div className="mbars">
                          <span className="mbar sales" style={{ height: `${Math.abs(s) / 최대 * 100}%` }}
                            title={`매출 ${Math.round(s / 1e6).toLocaleString()}백만`} />
                          <span className="mbar gross" style={{ height: `${Math.abs(g) / 최대 * 100}%` }}
                            title={`매출총이익 ${Math.round(g / 1e6).toLocaleString()}백만`} />
                        </div>
                        <div className="mlab">{m}월</div>
                        <div className="mval">{Math.round(s / 1e6).toLocaleString()}</div>
                        <div className="mval sub">{Math.round(g / 1e6).toLocaleString()}</div>
                        <div className={'mval sub' + (o < 0 ? ' neg' : '')}>{Math.round(o / 1e6).toLocaleString()}</div>
                      </div>
                    )
                  })}
                </div>
                <div className="mlegend">
                  <span><i className="sw sales" />매출액</span>
                  <span><i className="sw gross" />매출총이익</span>
                </div>
              </div>

              {/* 재고는 손익계산서 안에 있다 — 매출원가 밑 「기말 재고액」이 그 달 말 잔액이다.
                  돈이 물건으로 묶여 있는 자리라, 손익과 같이 봐야 읽힌다. */}
              {(() => {
                const { 상품, 제품 } = 재고줄들(rows)
                if (!상품 && !제품) return null
                const 달 = 쓰는달
                const 합 = 달.map((m) => (상품 ? 상품[`m${m}`] || 0 : 0) + (제품 ? 제품[`m${m}`] || 0 : 0))
                const 최대 = Math.max(1, ...합)
                const 끝 = 합[합.length - 1] || 0
                const 첫 = 합[0] || 0
                const 원가줄 = 찾기(rows, '매출원가')
                const 최근원가 = 달.slice(-3).reduce((a, m) => a + (원가줄 ? 원가줄[`m${m}`] || 0 : 0), 0) / 3
                return (
                  <div className="panel" style={{ padding: 16, marginBottom: 12 }}>
                    <b>재고 추이</b>
                    <span className="muted" style={{ fontSize: 12, marginLeft: 8 }}>
                      월말 재고(기말 재고액) · 백만원 · {div}
                    </span>
                    <div className="mchart">
                      <div className="mcol mkeys">
                        <div className="mbars" />
                        <div className="mlab">&nbsp;</div>
                        <div className="mval">재고 합계</div>
                        <div className="mval sub">상품</div>
                        <div className="mval sub">제품</div>
                      </div>
                      {달.map((m, i) => {
                        const g = 상품 ? 상품[`m${m}`] || 0 : 0
                        const pdt = 제품 ? 제품[`m${m}`] || 0 : 0
                        return (
                          <div key={m} className="mcol">
                            <div className="mbars">
                              <span className="mbar goods" style={{ height: `${g / 최대 * 100}%` }}
                                title={`상품 ${Math.round(g / 1e6).toLocaleString()}백만`} />
                              <span className="mbar prod" style={{ height: `${pdt / 최대 * 100}%` }}
                                title={`제품 ${Math.round(pdt / 1e6).toLocaleString()}백만`} />
                            </div>
                            <div className="mlab">{m}월</div>
                            <div className="mval">{Math.round(합[i] / 1e6).toLocaleString()}</div>
                            <div className="mval sub">{Math.round(g / 1e6).toLocaleString()}</div>
                            <div className="mval sub">{Math.round(pdt / 1e6).toLocaleString()}</div>
                          </div>
                        )
                      })}
                    </div>
                    <div className="mlegend">
                      <span><i className="sw goods" />상품 재고</span>
                      <span><i className="sw prod" />제품 재고</span>
                      <span style={{ marginLeft: 'auto' }}>
                        {달[0]}월 {Math.round(첫 / 1e6).toLocaleString()} →{' '}
                        {달[달.length - 1]}월 <b>{Math.round(끝 / 1e6).toLocaleString()}</b>백만
                        {첫 ? ` (${끝 >= 첫 ? '+' : ''}${(((끝 - 첫) / 첫) * 100).toFixed(1)}%)` : ''}
                        {최근원가 ? ` · 최근 3개월 평균 매출원가의 ${(끝 / 최근원가).toFixed(1)}개월치` : ''}
                      </span>
                    </div>
                  </div>
                )
              })()}

              <div className="row" style={{ alignItems: 'flex-start', gap: 12, flexWrap: 'wrap' }}>
                <div className="panel" style={{ padding: 16, flex: '1 1 420px' }}>
                  <b>사업부 비교</b>
                  <span className="muted" style={{ fontSize: 12, marginLeft: 8 }}>연간 누계 · 백만원</span>
                  <table style={{ marginTop: 10 }}>
                    <thead><tr>
                      <th className="l">사업부</th><th>매출액</th><th>매출총이익</th>
                      <th>영업이익</th><th>영업이익률</th>
                    </tr></thead>
                    <tbody>
                      {사업부표.map((x) => (
                        <tr key={x.이름} className={x.이름 === '전체' ? 'total' : ''}>
                          <td className="l">
                            {x.이름}{x.이름 === '전체' ? ' (회사)' : ''}
                            <div className="track" style={{ marginTop: 4 }}>
                              <span style={{ width: `${Math.abs(x.매출) / 사업부최대 * 100}%` }} />
                            </div>
                          </td>
                          <td className="fp-cell">{Math.round(x.매출 / 1e6).toLocaleString()}</td>
                          <td className="fp-cell">{Math.round(x.총이익 / 1e6).toLocaleString()}</td>
                          <td className={'fp-cell' + (x.영업 < 0 ? ' fp-bad' : '')}>
                            {Math.round(x.영업 / 1e6).toLocaleString()}
                          </td>
                          <td className={'fp-cell' + (x.영업 < 0 ? ' fp-bad' : '')}>
                            {x.매출 ? (x.영업 / x.매출 * 100).toFixed(1) + '%' : '—'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <div className="panel" style={{ padding: 16, flex: '1 1 320px' }}>
                  <b>판매비와 관리비 상위</b>
                  <span className="muted" style={{ fontSize: 12, marginLeft: 8 }}>백만원</span>
                  <table style={{ marginTop: 10 }}>
                    <tbody>
                      {판관상위.map((r) => (
                        <tr key={r['계정과목']}>
                          <td className="l" style={{ width: 130 }}>{r['계정과목']}</td>
                          <td>
                            <div className="track">
                              <span style={{ width: `${판관합 ? r['합계'] / 판관합 * 100 : 0}%` }} />
                            </div>
                          </td>
                          <td className="fp-cell" style={{ width: 74 }}>
                            {Math.round(r['합계'] / 1e6).toLocaleString()}
                          </td>
                          <td className="fp-cell muted" style={{ width: 52 }}>
                            {판관합 ? (r['합계'] / 판관합 * 100).toFixed(0) + '%' : ''}
                          </td>
                        </tr>
                      ))}
                      {!판관상위.length && <tr><td className="c muted">자료 없음</td></tr>}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* 분석은 맨 아래. 숫자를 먼저 보고 내려오면서 읽는 자리다. */}
              {!!분석줄들.length && (
                <div className="panel pb-why">
                  <div className="pb-why-h">
                    <b>분석</b>
                    <span className="muted">
                      올린 숫자에서 자동으로 뽑습니다 · 가결산 기준 · 백만원
                    </span>
                  </div>
                  <div className="pb-why-note">
                    사업부 비교는 <b>우열이 아니라 구조 차이</b>로 읽어 주세요 —
                    사업 성격과 성장 단계가 다르면 원가율도 판관비율도 다릅니다.
                    <div style={{ marginTop: 5 }}>
                      매출은 <b>상품매출↔상품매출원가 · 제품매출↔제품매출원가 ·
                      특정매입 매출↔특정매입원가</b>로 짝을 이루고,
                      <b> 그 밖의 매출은 원가가 없는 수수료성</b>입니다.
                      특정매입은 총매출에서 원가를 뺀 <b>순액만</b> 매출액에 들어갑니다.
                    </div>
                  </div>
                  <ul>
                    {분석줄들.map(([머리, 몸], i) => (
                      <li key={i}><b>{머리}</b><span>{몸}</span></li>
                    ))}
                  </ul>

                  {/* 사업부마다 따로. 전사 한 덩어리로만 적으면 제 사업부 이야기는
                      아무도 못 읽는다 - 읽는 사람이 거기부터 본다. */}
                  <div className="pb-why-div"
                    style={카드들.filter((c) => !c.전체인가).length ? undefined : { display: 'none' }}>
                    {카드들.filter((c) => !c.전체인가).map((c) => (
                      <div key={c.이름} className="pb-why-one">
                        <div className="h">{c.이름}</div>
                        <ul>
                          {사업부분석(c).map(([머리, 몸], i) => (
                            <li key={i}><b>{머리}</b><span>{몸}</span></li>
                          ))}
                        </ul>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {view === 'table' && (
          <>
          <div className="panel" style={{ overflowX: 'auto' }}>
            <table className="pnl">
              <thead>
                <tr>
                  <th className="l" style={{ minWidth: 190 }}>계정과목</th>
                  <th style={{ minWidth: 110 }}>합계</th>
                  {쓰는달.map((m) => <th key={m}>{m}월</th>)}
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr key={i} className={r.level === 0 ? 'pnl-head' : (r.level === 2 ? 'pnl-sub' : '')}>
                    <td className={'l pnl-l' + r.level}>{r['계정과목']}</td>
                    <td className="pnl-num pnl-sum">{show(r['합계'])}</td>
                    {쓰는달.map((m) => <td key={m} className="pnl-num">{show(r[`m${m}`])}</td>)}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="hint">
            {unit === '백만' ? '단위: 백만원' : '단위: 원'} · 값이 있는 달만 표시합니다.
          </div>
          </>
          )}
        </>
      )}
      {toast && <div className="toast">{toast}</div>}
    </>
  )
}
